import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function HowItWorksSection() {
  const { ref, isVisible } = useScrollAnimation();

  const steps = [
    {
      icon: 'ri-car-line',
      title: 'You Buy a Car',
      description: 'Choose from our quality pre-owned inventory and complete your purchase with confidence.',
      color: 'from-[#E8B84B] to-[#D4A43A]',
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'We Donate $100',
      description: 'Automatically, $100 from your sale goes directly to Oregon Dog Rescue—no strings attached.',
      color: 'from-[#4ADE80] to-[#22C55E]',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Dogs Get Help',
      description: 'Your contribution helps rescue, rehabilitate, and rehome dogs in need across the Pacific Northwest.',
      color: 'from-[#F472B6] to-[#EC4899]',
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Forever Homes',
      description: 'Thanks to your purchase, more dogs find loving families and the care they deserve.',
      color: 'from-[#60A5FA] to-[#3B82F6]',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#F7F5F0] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-full px-4 py-2 mb-4">
            <i className="ri-information-line text-[#E8B84B]"></i>
            <span className="text-sm font-semibold text-[#1C2B1E]">How It Works</span>
          </div>
          
          <h2 className="font-oswald text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
            The $100 Commitment
          </h2>
          
          <p className="text-base sm:text-lg text-[#4A5568] max-w-2xl mx-auto">
            Every single vehicle we sell triggers an automatic $100 donation to Oregon Dog Rescue. 
            It's that simple—and it's making a real difference.
          </p>
        </div>

        {/* Process Steps */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl p-6 border border-[#E8E2D6] hover:shadow-xl transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Step Number */}
              <div className="absolute -top-4 -left-4 w-9 h-9 bg-gradient-to-br from-[#1C2B1E] to-[#2C3E55] rounded-full flex items-center justify-center text-white font-bold text-base shadow-lg">
                {index + 1}
              </div>

              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-5 shadow-lg`}>
                <i className={`${step.icon} text-2xl text-white`}></i>
              </div>

              {/* Content */}
              <h3 className="font-oswald text-xl font-bold text-[#1C2B1E] mb-2">
                {step.title}
              </h3>
              <p className="text-[#4A5568] leading-relaxed text-sm">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Transparency Statement */}
        <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2C3E55] rounded-2xl p-6 sm:p-8 md:p-12 text-white text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-14 h-14 bg-[#E8B84B] rounded-full flex items-center justify-center mx-auto mb-5">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            
            <h3 className="font-oswald text-2xl sm:text-3xl font-bold mb-4">
              100% Transparent. 100% Committed.
            </h3>
            
            <p className="text-base sm:text-lg text-white/90 mb-6 leading-relaxed">
              We don't just talk about giving back—we prove it. Every donation is tracked, 
              and we share our cumulative impact publicly. No hidden fees, no fine print. 
              Just honest business and real impact.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center gap-2 bg-[#E8B84B] text-[#1C2B1E] px-6 py-3 rounded-lg font-semibold hover:bg-[#D4A43A] transition-colors whitespace-nowrap"
              >
                <i className="ri-car-line"></i>
                Shop &amp; Support
              </Link>
              <a
                href="https://www.oregondogrescue.org"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/30 px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Visit ODR
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}